import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCheckPromptBlocksMutation,
  type PromptBlockCheck,
} from '@/redux/internal-services/agentsApi';

interface Block { title?: string; prompt?: string }

export interface PromptChecks {
  /** Check result per system_prompts index (absent until the first check returns). */
  byIndex: Record<number, PromptBlockCheck>;
  errorCount: number;
  blocksWithErrors: number;
  checking: boolean;
  /** Run the check now and resolve with the fresh result (used before saving). */
  /** Run the check now; resolves with the problem counts (and shows the errors on the blocks). */
  checkNow: () => Promise<{ errorCount: number; blocksWithErrors: number }>;
  /** True once a check has run on the current text. */
  checked: boolean;
}

const EMPTY: PromptChecks = {
  byIndex: {}, errorCount: 0, blocksWithErrors: 0, checking: false, checked: false,
  checkNow: async () => ({ errorCount: 0, blocksWithErrors: 0 }),
};

export const PromptChecksContext = createContext<PromptChecks>(EMPTY);

/** Result of the latest template check for one prompt block (for the block's inline errors). */
export const usePromptBlockCheck = (index: number): PromptBlockCheck | undefined =>
  useContext(PromptChecksContext).byIndex[index];

export const usePromptChecksState = () => useContext(PromptChecksContext);

/** Server-side check of every prompt block (real Jinja environment, sample data), run on demand. Call once in the page and
 * hand the result to `PromptChecksContext.Provider` so every block (any tab) can show its own errors. */
export const usePromptChecksModel = (blocks: Block[]): PromptChecks => {
  const [check, { isLoading }] = useCheckPromptBlocksMutation();
  const [byIndex, setByIndex] = useState<Record<number, PromptBlockCheck>>({});
  const seq = useRef(0);

  // Rebuilt every render on purpose: react-hook-form's watch() hands back the SAME array object, mutated in place, so a
  // memo keyed on `blocks` would keep checking the text from before the edit.
  const payload = blocks.map((b) => ({ title: b.title ?? '', prompt: b.prompt ?? '' }));
  const key = JSON.stringify(payload);

  const run = async (): Promise<Record<number, PromptBlockCheck>> => {
    const mine = ++seq.current;
    const res = await check({ blocks: payload }).unwrap();
    const map: Record<number, PromptBlockCheck> = {};
    res.blocks.forEach((b) => { map[b.index] = b; });
    if (mine === seq.current) setByIndex(map);
    return map;
  };

  // Results describe the text that was checked: once a block is edited they are stale, so clear them rather than
  // show errors for text that is gone. A new check runs on the "Check prompts" button and before every save.
  useEffect(() => { setByIndex({}); }, [key]);

  return useMemo<PromptChecks>(() => {
    const list = Object.values(byIndex);
    return {
      byIndex,
      errorCount: list.reduce((n, b) => n + b.errors.length, 0),
      blocksWithErrors: list.filter((b) => !b.ok).length,
      checking: isLoading,
      checked: Object.keys(byIndex).length > 0,
      checkNow: async () => {
        const map = await run();
        const l = Object.values(map);
        return { errorCount: l.reduce((n, b) => n + b.errors.length, 0), blocksWithErrors: l.filter((b) => !b.ok).length };
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byIndex, isLoading, key]);
};
